import React, { useState, useEffect, useCallback } from 'react';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { ArrowLeft, X, Clock, Award, ChevronRight, ChevronDown, Images, ChevronLeft, Trash2 } from 'lucide-react';
import { YOGA_POSES } from '../data/yogaPoses';
import usePoseMedia from '../hooks/usePoseMedia';
import PoseMediaControls from './PoseMediaControls';
import UploadProgressOverlay from './UploadProgressOverlay';
import YogaVideoGallery from './YogaVideoGallery';

const LEVELS = { 'Débutant': '#10b981', 'Intermédiaire': '#f59e0b', 'Avancé': '#ef4444' };

const HIDDEN_KEY = 'yoga_hidden_images';

const getHiddenImages = () => {
  try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) || '{}'); } catch { return {}; }
};

const saveHiddenImages = (data) => {
  localStorage.setItem(HIDDEN_KEY, JSON.stringify(data));
};

const YogaPanel = ({ isOpen, onClose, isAdmin = false }) => {
  const [selectedPose, setSelectedPose] = useState(null);
  const [openSection, setOpenSection] = useState('benefits');
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [hiddenImages, setHiddenImages] = useState(getHiddenImages);
  const { media: poseMedia, uploads: poseUploads, upload: uploadMedia, remove: removeMedia, toggleLock: toggleMediaLock } = usePoseMedia('yoga');

  const getVisibleGallery = useCallback((pose) => {
    if (!pose?.gallery) return [];
    const hidden = hiddenImages[pose.id] || [];
    return pose.gallery.filter(img => !hidden.includes(img));
  }, [hiddenImages]);

  const handleDelete = (poseId, imgUrl) => {
    const updated = { ...hiddenImages };
    if (!updated[poseId]) updated[poseId] = [];
    updated[poseId].push(imgUrl);
    saveHiddenImages(updated);
    setHiddenImages(updated);
  };

  const handleRestore = (poseId) => {
    const updated = { ...hiddenImages };
    delete updated[poseId];
    saveHiddenImages(updated);
    setHiddenImages(updated);
    setGalleryIndex(0);
  };

  useEffect(() => {
    if (isOpen) { setSelectedPose(null); setOpenSection('benefits'); setGalleryIndex(0); }
  }, [isOpen]);

  const [keepAwake, toggleKeepAwake] = useKeepAwake('yoga', isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950" data-testid="yoga-panel">
      <div className="absolute inset-0 bg-black/95" onClick={onClose} />
      <div className="relative z-10 w-full h-full max-w-lg mx-auto flex flex-col" style={{ maxHeight: '100dvh' }}>

        {/* POSE LIST */}
        {!selectedPose && (
          <div className="flex flex-col h-full px-4 pt-12 pb-24 animate-fadeIn">
            <div className="text-center mb-6">
              <button onClick={onClose} className="absolute top-10 right-4 text-zinc-500 hover:text-white p-2 transition-colors" data-testid="yoga-close">
                <X size={22} />
              </button>
              <h2 className="text-2xl font-semibold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Postures de <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Yoga</span>
              </h2>
              <p className="text-zinc-500 text-sm">{YOGA_POSES.length} postures guidées</p>
              <div className="mt-3 flex justify-center">
                <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-yoga"
                  className="px-4 py-2 rounded-full text-xs font-medium bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/25" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pb-24 pt-3">
              {YOGA_POSES.map((pose) => {
                const visibleGallery = getVisibleGallery(pose);
                const mainImg = visibleGallery[0] || pose.image;
                const customVideo = poseMedia[pose.id];
                return (
                <button key={pose.id} onClick={() => { setSelectedPose(pose); setGalleryIndex(0); }}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all active:scale-[0.98]"
                  data-testid={`pose-${pose.id}`}>
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-slate-800 relative">
                    {customVideo ? (
                      <video src={customVideo.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : (
                      <img src={mainImg} alt={pose.name} className="w-full h-full object-cover" loading="lazy" />
                    )}
                    {customVideo && (
                      <div className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full bg-red-500 ring-2 ring-slate-900" title="Vidéo personnalisée" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white font-medium text-sm">{pose.name}</p>
                    <p className="text-white/40 text-xs italic">{pose.sanskrit}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${LEVELS[pose.level]}20`, color: LEVELS[pose.level] }}>
                        {pose.level}
                      </span>
                      <span className="text-white/30 text-xs flex items-center gap-1">
                        <Clock size={10} /> {pose.duration}
                      </span>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-white/20" />
                </button>
                );
              })}
            </div>
          </div>
        )}

        {/* POSE DETAIL */}
        {selectedPose && (() => {
          const visibleGallery = getVisibleGallery(selectedPose);
          const safeIndex = Math.min(galleryIndex, visibleGallery.length - 1);
          const currentImg = visibleGallery[safeIndex] || selectedPose.image;
          const hiddenCount = (hiddenImages[selectedPose.id] || []).length;
          const customVideo = poseMedia[selectedPose.id];
          return (
          <div className="flex flex-col h-full animate-fadeIn" data-testid="pose-detail">
            {/* Header media (custom video if uploaded, else gallery image) */}
            <div className="relative h-56 flex-shrink-0">
              {customVideo ? (
                <video
                  src={customVideo.url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  className="w-full h-full object-cover bg-black"
                  data-testid={`pose-video-${selectedPose.id}`}
                />
              ) : (
                <img
                  src={currentImg}
                  alt={`${selectedPose.name} - ${safeIndex + 1}`}
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/80 pointer-events-none" />

              {/* Admin: per-pose video controls */}
              {isAdmin && (
                <PoseMediaControls
                  poseId={selectedPose.id}
                  hasMedia={!!customVideo}
                  locked={!!customVideo?.locked}
                  onUpload={(file) => uploadMedia(selectedPose.id, file)}
                  onDelete={() => removeMedia(selectedPose.id)}
                  onToggleLock={() => toggleMediaLock(selectedPose.id)}
                />
              )}

              {/* Upload progress overlay */}
              {poseUploads?.[selectedPose.id] && (
                <UploadProgressOverlay info={poseUploads[selectedPose.id]} poseId={selectedPose.id} />
              )}

              {/* Gallery prev/next arrows (hidden when custom video active) */}
              {!customVideo && visibleGallery.length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIndex((safeIndex - 1 + visibleGallery.length) % visibleGallery.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 transition-colors z-20"
                    data-testid="gallery-prev">
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setGalleryIndex((safeIndex + 1) % visibleGallery.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 transition-colors z-20"
                    data-testid="gallery-next">
                    <ChevronRight size={18} />
                  </button>
                  {/* Counter */}
                  <div className="absolute top-4 right-14 bg-black/50 backdrop-blur-sm text-white/80 text-xs px-2.5 py-1 rounded-full z-20 flex items-center gap-1">
                    <Images size={12} />
                    {safeIndex + 1}/{visibleGallery.length}
                  </div>
                </>
              )}

              {/* Admin delete current image (hidden when custom video active) */}
              {!customVideo && isAdmin && visibleGallery.length > 1 && (
                <button
                  onClick={() => {
                    handleDelete(selectedPose.id, currentImg);
                    if (safeIndex >= visibleGallery.length - 1) setGalleryIndex(Math.max(0, safeIndex - 1));
                  }}
                  className="absolute bottom-16 right-4 bg-red-600/80 backdrop-blur-sm text-white rounded-full px-3 py-1.5 flex items-center gap-1.5 hover:bg-red-500 transition-colors z-20 text-xs"
                  data-testid="admin-delete-img">
                  <Trash2 size={14} />
                  Supprimer
                </button>
              )}

              <button onClick={() => { setSelectedPose(null); setGalleryIndex(0); }}
                className="absolute top-10 left-4 flex items-center gap-2 text-white/90 hover:text-white bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm z-20"
                data-testid="pose-back">
                <ArrowLeft size={18} />
                <span className="text-sm">Retour</span>
              </button>
              <div className="absolute bottom-4 left-4 right-4 z-10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2.5 py-0.5 rounded-full"
                    style={{ background: `${LEVELS[selectedPose.level]}30`, color: LEVELS[selectedPose.level] }}>
                    {selectedPose.level}
                  </span>
                  <span className="text-white/60 text-xs flex items-center gap-1">
                    <Clock size={11} /> {selectedPose.duration}
                  </span>
                </div>
                <h3 className="text-white text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {selectedPose.name}
                </h3>
                <p className="text-amber-400/80 text-sm italic">{selectedPose.sanskrit}</p>
              </div>
            </div>

            {/* Gallery Thumbnails (hidden when custom video active) */}
            {!customVideo && visibleGallery.length > 1 && (
              <div className="flex gap-2 px-4 py-3 overflow-x-auto flex-shrink-0" data-testid="gallery-thumbnails">
                {visibleGallery.map((img, i) => (
                  <div key={`${selectedPose.id}-thumb-${i}`} className="relative flex-shrink-0">
                    <button onClick={() => setGalleryIndex(i)}
                      className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === safeIndex ? 'border-amber-400 scale-105' : 'border-white/10 opacity-60 hover:opacity-90'}`}
                      data-testid={`thumb-${i}`}>
                      <img src={img} alt={`Option ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                    {isAdmin && visibleGallery.length > 1 && (
                      <button
                        onClick={() => {
                          handleDelete(selectedPose.id, img);
                          if (safeIndex >= i && safeIndex > 0) setGalleryIndex(safeIndex - 1);
                        }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-500 z-10"
                        data-testid={`thumb-delete-${i}`}>
                        <X size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Admin restore button */}
            {isAdmin && hiddenCount > 0 && (
              <div className="px-4 pb-2">
                <button
                  onClick={() => handleRestore(selectedPose.id)}
                  className="w-full py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-medium hover:bg-amber-500/25 transition-colors"
                  data-testid="admin-restore-imgs">
                  Restaurer les {hiddenCount} image{hiddenCount > 1 ? 's' : ''} supprimée{hiddenCount > 1 ? 's' : ''}
                </button>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {/* Benefits */}
              <button onClick={() => setOpenSection(openSection === 'benefits' ? '' : 'benefits')}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20"
                data-testid="toggle-benefits">
                <div className="flex items-center gap-2">
                  <Award size={18} className="text-emerald-400" />
                  <span className="text-white font-medium text-sm">Pourquoi cette posture ?</span>
                </div>
                <ChevronDown size={18} className={`text-white/40 transition-transform ${openSection === 'benefits' ? 'rotate-180' : ''}`} />
              </button>
              {openSection === 'benefits' && (
                <div className="pl-3 space-y-2 animate-fadeIn">
                  {selectedPose.benefits.map((b, i) => (
                    <div key={b} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
                      <p className="text-white/70 text-sm">{b}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Instructions */}
              <button onClick={() => setOpenSection(openSection === 'howto' ? '' : 'howto')}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-amber-500/10 border border-amber-500/20"
                data-testid="toggle-howto">
                <div className="flex items-center gap-2">
                  <span className="text-amber-400 text-lg">&#9755;</span>
                  <span className="text-white font-medium text-sm">Comment faire ?</span>
                </div>
                <ChevronDown size={18} className={`text-white/40 transition-transform ${openSection === 'howto' ? 'rotate-180' : ''}`} />
              </button>
              {openSection === 'howto' && (
                <div className="space-y-3 animate-fadeIn">
                  {selectedPose.instructions.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 pl-1">
                      <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-white/70 text-sm leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Video gallery (multi-videos: YouTube + custom uploads) */}
              <div className="pt-2 border-t border-white/5 mt-2">
                <YogaVideoGallery poseId={selectedPose.id} isAdmin={isAdmin} />
              </div>
            </div>
          </div>
          );
        })()}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.35s ease forwards; }
      `}</style>
    </div>
  );
};

export default YogaPanel;
